import { primeUserBranchesCache } from '../../auth/application/authBranches';
import { primeSessionEmployeeCache } from '../../auth/application/sessionEmployeeResolver';
import {
  getOrganizationBusinessProfileOffline,
  saveOrganizationBusinessProfileOffline,
  saveBranchCapabilitiesJson,
} from '../../shell/connectivity/businessProfileCache';
import { useBusinessProfileStore } from '../session/store/useBusinessProfileStore';
import {
  fetchBootstrapMobileSession,
  primeBootstrapSessionCache,
  type BootstrapMobileSessionPayload,
} from './bootstrapMobileSessionService';
import { primeAccountSetupFromBootstrap } from './accountSetupCache';
import { primeGlobalFeatureFlagsCache } from '../feature-flags/globalFeatureFlagsService';
import { primeOrganizationEntitlementsCache } from '../feature-flags/planEntitlementsService';
import { primeSessionOrganizationFromEntitlements } from './sessionOrganization';
import { primeMyOrganizationsCache } from './organizationSwitcherService';
import { primeTrustStatusCache } from '../../settings/infra/orgTrustStatusService';
import { broadcastSessionCache } from './sessionCacheBroadcast';
import { logSessionNetworkSummary } from '../../shared/dev/networkCallAudit';
import { markBoot } from '../../shared/dev/bootTiming';

export type { BootstrapMobileSessionPayload };

let sessionBootstrapGate: {
  userId: string;
  promise: Promise<BootstrapMobileSessionPayload | null>;
} | null = null;

export function clearSessionBootstrapGate(): void {
  sessionBootstrapGate = null;
}

/**
 * Gate único de arranque: AccountSetup, entitlements, flags y App esperan el mismo promise.
 * Sin dynamic import (un tick menos) y sin segundo RPC por `force` en cold start.
 */
export function awaitSessionBootstrap(
  userId: string,
  options?: { force?: boolean },
): Promise<BootstrapMobileSessionPayload | null> {
  if (!options?.force && sessionBootstrapGate?.userId === userId) {
    return sessionBootstrapGate.promise;
  }
  const promise = hydrateSessionCachesFromBootstrap(userId, options);
  sessionBootstrapGate = { userId, promise };
  return promise;
}

async function persistBusinessProfileOffline(
  profile: NonNullable<BootstrapMobileSessionPayload['business_profile']>,
  branches: BootstrapMobileSessionPayload['branches'],
): Promise<void> {
  await saveOrganizationBusinessProfileOffline(profile);
  if (!profile.capabilities?.length || !branches?.length) return;
  for (const branch of branches) {
    try {
      await saveBranchCapabilitiesJson(branch.id, profile.capabilities);
    } catch (branchErr) {
      console.warn('[hydrateSessionCachesFromBootstrap] branch capabilities save failed', branch.id, branchErr);
    }
  }
}

/**
 * Una llamada RPC de bootstrap y prime de caches compartidos (entitlements, flags, branches, employee).
 * Persistencia SQLite del business_profile en background; restore offline si el RPC no trae profile.
 */
export async function hydrateSessionCachesFromBootstrap(
  userId: string,
  options?: { force?: boolean },
): Promise<BootstrapMobileSessionPayload | null> {
  markBoot('bootstrap_rpc_start');
  const payload = await fetchBootstrapMobileSession(userId, options);
  markBoot(payload ? 'bootstrap_rpc_ok' : 'bootstrap_rpc_empty');
  if (!payload) return null;

  primeBootstrapSessionCache(userId, payload);
  if (payload.account_setup) {
    // Merge organization_closed_at (top-level in bootstrap payload) into account_setup
    // so AccountSetupState carries it for AppNavigator's closed-business interception.
    // Va como organizationClosedAt: account_setup ya viene normalizado (camelCase)
    // por parseAccountSetupPayload y primeAccountSetupFromBootstrap lo toma as-is.
    const setupWithClosedAt = {
      ...payload.account_setup,
      organizationClosedAt:
        payload.organization_closed_at ?? payload.account_setup.organizationClosedAt,
    };
    primeAccountSetupFromBootstrap(userId, setupWithClosedAt);
  }
  primeOrganizationEntitlementsCache(payload.entitlements);
  primeGlobalFeatureFlagsCache(payload.feature_flags);
  primeUserBranchesCache(payload.branches);
  primeSessionEmployeeCache(userId, payload.employee);
  // Secciones plegadas del boot (20262024200000): orgs del switcher + trust.
  // Solo prima valores presentes: server viejo (undefined) o sección fallida
  // (null) dejan el cache libre para que el lector caiga a la RPC directa.
  if (payload.organizations != null) {
    primeMyOrganizationsCache(payload.organizations);
  }
  if (payload.org_trust != null) {
    primeTrustStatusCache(payload.org_trust);
  }
  if (payload.entitlements) {
    primeSessionOrganizationFromEntitlements(userId, payload.entitlements);
  }

  // No pisar ni borrar offline con null: bootstrap sin org (onboarding) o parse fallido.
  if (payload.business_profile) {
    useBusinessProfileStore.getState().setProfile(payload.business_profile);
    void persistBusinessProfileOffline(payload.business_profile, payload.branches).catch((e) => {
      console.warn('[hydrateSessionCachesFromBootstrap] business profile offline save failed', e);
    });
  } else if (!useBusinessProfileStore.getState().profile) {
    try {
      const offline = await getOrganizationBusinessProfileOffline();
      if (offline) useBusinessProfileStore.getState().setProfile(offline);
    } catch (e) {
      console.warn('[hydrateSessionCachesFromBootstrap] offline business profile restore failed', e);
    }
  }

  broadcastSessionCache({ type: 'entitlements', payload: payload.entitlements });
  broadcastSessionCache({ type: 'feature_flags', payload: payload.feature_flags });
  broadcastSessionCache({ type: 'branches', payload: payload.branches });

  logSessionNetworkSummary();
  markBoot('bootstrap_hydrated');
  return payload;
}
